import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SensorsIcon from "@mui/icons-material/Sensors";
import MemoryIcon from "@mui/icons-material/Memory";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import CloudIcon from "@mui/icons-material/Cloud";
import { useNavigate } from "react-router-dom";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { motion } from "framer-motion";

// ✅ IoT & Embedded Content Data
const iotEmbeddedContent = [
  {
    section: "Microcontrollers & Boards",
    icon: <DeveloperBoardIcon />,
    items: [
      {
        name: "Arduino (Beginner)",
        description: "Popular microcontroller platform for prototyping.",
        fullContent: [
          {
            heading: "Overview",
            text: "Arduino is open-source, beginner-friendly, and uses C/C++. Perfect for beginners in IoT and robotics.",
          },
          {
            heading: "Applications",
            points: ["LED blinking", "Home automation", "Robotics projects"],
          },
          {
            heading: "Example Code",
            code: `// Blink LED on Arduino Pin 13
void setup() {
  pinMode(13, OUTPUT);
}
void loop() {
  digitalWrite(13, HIGH);
  delay(1000);
  digitalWrite(13, LOW);
  delay(1000);
}`,
            language: "cpp",
          },
        ],
      },
      {
        name: "Raspberry Pi (Intermediate)",
        description: "Single-board computer for advanced embedded projects.",
        fullContent: [
          {
            heading: "Overview",
            text: "Raspberry Pi is a Linux-based mini-computer. Supports Python and can connect to sensors, cameras, and the cloud.",
          },
          {
            heading: "Applications",
            points: ["Smart cameras", "IoT gateways", "Media centers"],
          },
          {
            heading: "Example Code (Python)",
            code: `import RPi.GPIO as GPIO
import time
GPIO.setmode(GPIO.BCM)
GPIO.setup(18, GPIO.OUT)
while True:
    GPIO.output(18, True)
    time.sleep(1)
    GPIO.output(18, False)
    time.sleep(1)`,
            language: "python",
          },
        ],
      },
    ],
  },
  {
    section: "IoT Protocols & Communication",
    icon: <SensorsIcon />,
    items: [
      {
        name: "MQTT (Intermediate)",
        description: "Lightweight messaging protocol for IoT.",
        fullContent: [
          {
            heading: "Overview",
            text: "MQTT uses Publisher/Subscriber model. Ideal for low-power IoT devices.",
          },
          {
            heading: "Example (Python MQTT Publish)",
            code: `import paho.mqtt.client as mqtt
client = mqtt.Client()
client.connect("broker.hivemq.com", 1883, 60)
client.publish("iot/topic", "Hello IoT World!")`,
            language: "python",
          },
        ],
      },
      {
        name: "HTTP & CoAP",
        description: "Standard and lightweight IoT protocols.",
        fullContent: [
          {
            heading: "Overview",
            points: [
              "HTTP: Common for web-based IoT apps",
              "CoAP: Lightweight, optimized for constrained devices",
            ],
          },
          {
            heading: "Example (Web3.js fetching IoT data)",
            code: `const Web3 = require("web3");
const web3 = new Web3("http://localhost:8545");

(async () => {
  const accounts = await web3.eth.getAccounts();
  console.log("Connected IoT Wallet:", accounts[0]);
})();`,
            language: "javascript",
          },
        ],
      },
    ],
  },
  {
    section: "Embedded Systems Programming",
    icon: <MemoryIcon />,
    items: [
      {
        name: "C/C++ (Low-Level)",
        description: "Efficient programming for real-time systems.",
        fullContent: [
          {
            heading: "Overview",
            text: "C/C++ provides direct hardware access, memory efficiency, and low latency operations.",
          },
          {
            heading: "Example Code",
            code: `#include <avr/io.h>
int main(void) {
  DDRB |= (1 << PB5);
  while(1) {
    PORTB ^= (1 << PB5);
    for (long i=0; i<100000; i++);
  }
}`,
            language: "cpp",
          },
        ],
      },
      {
        name: "Python for IoT",
        description: "High-level IoT scripting on devices like Raspberry Pi.",
        fullContent: [
          {
            heading: "Overview",
            points: [
              "Rapid prototyping",
              "Cloud & sensor libraries available",
              "Easy integration with AI & ML",
            ],
          },
          {
            heading: "Example Code",
            code: `import requests
sensor_value = 28.5
requests.post("http://iot-server/data", json={"temp": sensor_value})`,
            language: "python",
          },
        ],
      },
    ],
  },
  {
    section: "Cloud & IoT Integration",
    icon: <CloudIcon />,
    items: [
      {
        name: "AWS IoT Core (Advanced)",
        description: "Manage IoT devices on the cloud securely.",
        fullContent: [
          {
            heading: "Overview",
            points: [
              "Device shadowing & registry",
              "Secure MQTT communication",
              "Integrates with AWS Lambda & Analytics",
            ],
          },
          {
            heading: "Example AWS IoT Policy",
            code: `{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Action": ["iot:*"],
    "Resource": ["*"]
  }]
}`,
            language: "json",
          },
        ],
      },
      {
        name: "Azure IoT Hub (Advanced)",
        description: "Enterprise IoT platform by Microsoft.",
        fullContent: [
          {
            heading: "Overview",
            points: [
              "Real-time telemetry",
              "AI-driven anomaly detection",
              "Integrates with Azure Digital Twins",
            ],
          },
          {
            heading: "Example (Node.js Azure IoT SDK)",
            code: `const { Client } = require("azure-iot-device-mqtt");
const { Message } = require("azure-iot-device");

const connectionString = "<device-connection-string>";
const client = Client.fromConnectionString(connectionString);

setInterval(() => {
  const msg = new Message(JSON.stringify({ temp: 30 }));
  client.sendEvent(msg);
}, 2000);`,
            language: "javascript",
          },
        ],
      },
    ],
  },
];

function IoTEmbedded() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ bgcolor: "#f4f6f8", minHeight: "100vh", pt: 12, pb: 8 }}>
      <Container maxWidth="xl">
        {/* Header Navigation */}
        <Box mb={4} display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              fontWeight: 700,
              color: "#034522",
              mr: 2,
              textTransform: "none",
              borderRadius: "50px",
              px: 3,
              "&:hover": { bgcolor: "rgba(3, 69, 34, 0.1)" },
            }}
          >
            Back to Services
          </Button>
          <Typography variant="h4" fontWeight={800} color="#1a1a1a" sx={{ fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
            IoT & Embedded Systems
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Sidebar Tabs (Desktop) / Top Tabs (Mobile) */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                background: "#fff",
              }}
            >
              <Tabs
                orientation={isMobile ? "horizontal" : "vertical"}
                variant="scrollable"
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  borderRight: { md: 1, xs: 0 },
                  borderColor: "divider",
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1A9E7F",
                    width: "4px",
                    left: { md: 0 },
                    right: "auto"
                  },
                }}
              >
                {iotEmbeddedContent.map((section, index) => (
                  <Tab
                    key={index}
                    icon={<Box sx={{ color: activeTab === index ? "#1A9E7F" : "inherit" }}>{section.icon}</Box>}
                    iconPosition="start"
                    label={section.section}
                    sx={{
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontWeight: 600,
                      color: activeTab === index ? "#034522" : "#666",
                      fontSize: "1rem",
                      py: 3,
                      px: 3,
                      minHeight: 64,
                      transition: "all 0.3s",
                      "&.Mui-selected": {
                        color: "#034522",
                        bgcolor: "rgba(26, 158, 127, 0.08)",
                      },
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.02)",
                      }
                    }}
                  />
                ))}
              </Tabs>
            </Paper>
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={9}>
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  minHeight: "60vh",
                  bgcolor: "#fff"
                }}
              >
                <Box mb={4} display="flex" alignItems="center">
                    <Box 
                        sx={{ 
                            p: 1.5, 
                            borderRadius: '12px', 
                            bgcolor: 'rgba(3, 69, 34, 0.1)', 
                            color: '#034522',
                            mr: 2,
                            display: 'flex',
                            boxShadow: '0 4px 12px rgba(3, 69, 34, 0.1)'
                        }}
                    >
                        {iotEmbeddedContent[activeTab].icon}
                    </Box>
                    <Typography variant="h5" fontWeight={800} color="#034522">
                    {iotEmbeddedContent[activeTab].section}
                    </Typography>
                </Box>
                
                <Box>
                  {iotEmbeddedContent[activeTab].items.map((item, i) => (
                    <Accordion
                      key={i}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: 2,
                        borderRadius: "16px !important",
                        border: "1px solid #edf2f7",
                        "&:before": { display: "none" },
                        "&:hover": { bgcolor: "#f8f9fa", borderColor: "#e2e8f0" },
                        transition: "all 0.2s"
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: "#1A9E7F" }} />}
                        sx={{ px: 3, py: 1 }}
                      >
                        <Box>
                          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', color: '#1a1a1a' }}>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="#666" sx={{ mt: 0.5 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pb: 4, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                        {item.fullContent.map((content, idx) => (
                          <Box key={idx} mt={3}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              gutterBottom
                              color="#1A9E7F"
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1A9E7F' }} />
                                {content.heading}
                            </Typography>
                            {content.text && (
                              <Typography paragraph variant="body2" color="#4a5568" mb={2} sx={{ lineHeight: 1.7 }}>
                                {content.text}
                              </Typography>
                            )}
                            {content.points && (
                                <ul style={{ marginLeft: "1.5rem", marginTop: 0, marginBottom: '16px', color: '#4a5568' }}>
                                    {content.points.map((point, pIdx) => (
                                        <li key={pIdx}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{point}</Typography>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {content.code && (
                              <Box
                                sx={{
                                  borderRadius: 3,
                                  overflow: "hidden",
                                  border: "1px solid #e2e8f0",
                                  marginTop: 1,
                                  boxShadow: "0 4px 6px rgba(0,0,0,0.02)"
                                }}
                              >
                                <SyntaxHighlighter
                                  language={content.language || "javascript"}
                                  style={atomDark}
                                  customStyle={{ margin: 0, padding: "20px", fontSize: "0.9rem", lineHeight: 1.5 }}
                                >
                                  {content.code}
                                </SyntaxHighlighter>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default IoTEmbedded;
